import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import BulkCertificatePrintDialog from "@/components/BulkCertificatePrintDialog";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Download, Lock, Pencil, Trash2, X, Check,
  CircleDot, CircleDashed, CircleCheck, Sparkles, Calendar, User2, FileCheck2,
  Library, GitCompareArrows, ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Save, Archive,
  Search, ChevronDown, ChevronUp, Award
} from "lucide-react";

const STATUS_COLORS = {
  0: { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-300", label: "لم يحقق", icon: X },
  1: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300", label: "قيد التقدم", icon: CircleDashed },
  2: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300", label: "تم تحقيقه", icon: Check },
};

function StatusButton({ current, value, onClick, testid }) {
  const S = STATUS_COLORS[value];
  const Icon = S.icon;
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition-all ${
        active
          ? `${S.bg} ${S.text} ${S.border} shadow-sm scale-105`
          : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {S.label}
    </button>
  );
}

function BulletEditor({ label, items, setItems, suggestions = [], testidPrefix, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = (val) => {
    const v = (val || draft).trim();
    if (!v) return;
    if (items.includes(v)) return;
    setItems([...items, v]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <Label className="font-bold text-slate-700">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 text-sky-800 rounded-full px-3 py-1 text-sm">
            <span>{it}</span>
            <button
              type="button"
              onClick={() => setItems(items.filter((_, idx) => idx !== i))}
              data-testid={`${testidPrefix}-remove-${i}`}
              className="hover:bg-sky-200 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-slate-400">لا توجد بنود بعد</span>}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testidPrefix}-input`}
          className="flex-1"
        />
        <Button type="button" onClick={() => add()} data-testid={`${testidPrefix}-add`} className="gap-1 bg-slate-800 hover:bg-slate-900">
          <Plus className="w-4 h-4" /> إضافة
        </Button>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          <span className="text-[11px] font-bold text-slate-500 me-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> اقتراحات:
          </span>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => add(s)}
              data-testid={`${testidPrefix}-suggest-${i}`}
              className="text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-2.5 py-1 transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EvaluationDialog({ open, onOpenChange, child, existing, domainsData, onSaved }) {
  const [tab, setTab] = useState("skills");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [programType, setProgramType] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [statuses, setStatuses] = useState({}); // { "DOMAIN_SKILL": status }
  const [notes, setNotes] = useState({}); // { "DOMAIN_SKILL": "..." }
  const [strengths, setStrengths] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [conclusion, setConclusion] = useState("");
  const [saving, setSaving] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({}); // { "DOMAIN::group": true }
  const [collapsedDomains, setCollapsedDomains] = useState({}); // { "DOMAIN": true }
  const [masteredKeys, setMasteredKeys] = useState(new Set()); // {"DOMAIN::SKILL"}

  const loadMastered = async () => {
    if (!child) return;
    try {
      const { data } = await api.get(`/children/${child.id}/mastered-skills`);
      setMasteredKeys(new Set((data.mastered_skills || []).map((m) => m.key || `${m.domain_id}::${m.skill_id}`)));
    } catch { /* silent */ }
  };
  useEffect(() => { if (open && child) loadMastered(); }, [open, child]);

  const toggleMaster = async (domain_id, skill_id) => {
    if (!child) return;
    const key = `${domain_id}::${skill_id}`;
    const isNow = !masteredKeys.has(key);
    try {
      await api.patch(`/children/${child.id}/mastered-skills`, { domain_id, skill_id, mastered: isNow });
      setMasteredKeys((s) => {
        const n = new Set(s);
        if (isNow) n.add(key); else n.delete(key);
        return n;
      });
      toast.success(isNow ? "🏆 تم اعتماد المهارة مدى الحياة" : "أُزيل الاعتماد");
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDate(existing.date || new Date().toISOString().slice(0, 10));
      setProgramType(existing.program_type || "");
      setPeriodLabel(existing.period_label || "");
      const st = {}, nt = {};
      (existing.entries || []).forEach((e) => {
        const k = `${e.domain_id}_${e.skill_id}`;
        st[k] = e.status;
        if (e.notes) nt[k] = e.notes;
      });
      setStatuses(st); setNotes(nt);
      setStrengths(existing.strengths || []);
      setChallenges(existing.challenges || []);
      setRecommendations(existing.recommendations || []);
      setConclusion(existing.conclusion_summary || "");
    } else {
      setDate(new Date().toISOString().slice(0, 10));
      setProgramType(""); setPeriodLabel(""); setStatuses({}); setNotes({});
      setStrengths([]); setChallenges([]); setRecommendations([]); setConclusion("");
    }
    setTab("skills");
  }, [open, existing]);

  const domains = domainsData?.domains || [];

  const stats = useMemo(() => {
    return domains.map((d) => {
      let s0 = 0, s1 = 0, s2 = 0;
      d.skills.forEach((s) => {
        const v = statuses[`${d.domain_id}_${s.skill_id}`];
        if (v === 2) s2++;
        else if (v === 1) s1++;
        else if (v === 0) s0++;
      });
      const total = d.skills.length;
      const pct = total ? Math.round(((s2 * 100 + s1 * 50) / (total * 100)) * 100) : 0;
      return { ...d, s0, s1, s2, total, percent: pct };
    });
  }, [statuses, domains]);

  const overall = stats.length ? Math.round(stats.reduce((a, x) => a + x.percent, 0) / stats.length) : 0;

  const save = async () => {
    if (!child) return;
    const entries = [];
    for (const d of domains) {
      for (const s of d.skills) {
        const k = `${d.domain_id}_${s.skill_id}`;
        if (statuses[k] !== undefined) {
          entries.push({ domain_id: d.domain_id, skill_id: s.skill_id, status: statuses[k], notes: notes[k] || "" });
        }
      }
    }
    const payload = {
      child_id: child.id, date, program_type: programType, period_label: periodLabel,
      entries, strengths, challenges, recommendations, conclusion_summary: conclusion,
    };
    setSaving(true);
    try {
      if (existing) {
        await api.patch(`/evaluations/${existing.id}`, payload);
        toast.success("تم تحديث التقييم");
      } else {
        await api.post("/evaluations", payload);
        toast.success("تم حفظ التقييم");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const setStatus = (key, val) => setStatuses((s) => ({ ...s, [key]: val }));
  const setNote = (key, val) => setNotes((n) => ({ ...n, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="evaluation-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-sky-600" />
            {existing ? "تعديل تقييم" : "تقييم جديد"} — {child?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="space-y-1.5">
            <Label className="font-bold">تاريخ التقييم</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="eval-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-bold">نوع البرنامج</Label>
            <Input value={programType} onChange={(e) => setProgramType(e.target.value)} placeholder="مثال: البرنامج المسائي" data-testid="eval-program" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-bold">الفترة</Label>
            <Input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="مثال: الفصل الأول 2026" data-testid="eval-period" />
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-l from-sky-50 to-emerald-50 border border-sky-200 p-4 mt-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-sky-700 uppercase tracking-wider">النسبة الإجمالية</div>
            <div className="text-4xl font-black text-sky-700 mt-1" data-testid="eval-overall-percent">{overall}%</div>
          </div>
          <div className="hidden md:grid grid-cols-4 lg:grid-cols-7 xl:grid-cols-13 gap-2 flex-1 mx-4">
            {stats.map((s) => {
              const shortMap = { MOTOR: "حركي", HYDRO: "حسي/مائي", SOCIAL: "اجتماعي", ADL: "حياتي", ACADEMIC: "أكاديمي", ARABIC: "عربي", ENGLISH: "English", MATH: "رياضيات", SCIENCE: "علوم", RELIGION: "ديني", RECREATIONAL: "فنون", VOCATIONAL: "مهني", EMOTIONAL: "عاطفي" };
              const label = shortMap[s.domain_id] || (s.name || "").split(" ")[0];
              return (
                <div key={s.domain_id} className="text-center" title={s.name}>
                  <div className="text-[10px] font-bold text-slate-600 truncate">{label}</div>
                  <div className="text-xs font-black" style={{ color: s.color }}>{s.percent}%</div>
                  <div className="h-1.5 bg-slate-200 rounded-full mt-1 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${s.percent}%`, background: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="inline-flex bg-slate-100 rounded-xl p-1 mt-4">
          <TabBtn active={tab === "skills"} onClick={() => setTab("skills")} testid="tab-skills">المهارات</TabBtn>
          <TabBtn active={tab === "qualitative"} onClick={() => setTab("qualitative")} testid="tab-qualitative">التقييم النوعي</TabBtn>
        </div>

        {tab === "skills" && (
          <div className="space-y-5 mt-3">
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b pb-2 flex items-center gap-2 flex-wrap" data-testid="skills-toolbar">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={skillSearch}
                  onChange={(e) => setSkillSearch(e.target.value)}
                  placeholder="ابحث عن مهارة أو مجموعة..."
                  className="pr-9 h-10"
                  data-testid="skill-search"
                />
              </div>
              <div className="text-xs font-black text-slate-500 bg-slate-100 rounded-full px-3 h-8 flex items-center">
                {domains.reduce((n, d) => n + d.skills.length, 0)} مهارة · {domains.length} مجالات
              </div>
              <Button size="sm" variant="outline" onClick={() => setCollapsedDomains(Object.fromEntries(domains.map((d) => [d.domain_id, true])))} data-testid="collapse-all-btn">
                طيّ الكل
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCollapsedDomains({})} data-testid="expand-all-btn">
                فتح الكل
              </Button>
            </div>
            {domains.map((d) => {
              const q = skillSearch.trim().toLowerCase();
              // Filter skills by search
              const filteredSkills = q
                ? d.skills.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.group || "").toLowerCase().includes(q))
                : d.skills;
              if (q && filteredSkills.length === 0) return null;
              // Group skills by group field
              const groups = {};
              filteredSkills.forEach((s) => {
                const g = s.group || "غير مصنّف";
                if (!groups[g]) groups[g] = [];
                groups[g].push(s);
              });
              const domainCollapsed = !!collapsedDomains[d.domain_id] && !q;
              const domStats = stats.find((x) => x.domain_id === d.domain_id);
              return (
                <div key={d.domain_id} className="rounded-xl border-2 overflow-hidden" style={{ borderColor: `${d.color}30` }} data-testid={`domain-${d.domain_id}`}>
                  <button
                    type="button"
                    onClick={() => setCollapsedDomains((c) => ({ ...c, [d.domain_id]: !c[d.domain_id] }))}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors text-right"
                    data-testid={`domain-toggle-${d.domain_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {domainCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                      <div className="font-black text-lg" style={{ color: d.color }}>{d.name}</div>
                      <span className="text-[11px] font-black text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{filteredSkills.length}/{d.skills.length}</span>
                    </div>
                    <div className="text-sm font-bold" style={{ color: d.color }}>{domStats?.percent || 0}%</div>
                  </button>
                  {!domainCollapsed && (
                    <div className="p-4 pt-0 space-y-4">
                      {Object.entries(groups).map(([groupName, skills]) => {
                        const gKey = `${d.domain_id}::${groupName}`;
                        const gCollapsed = !!collapsedGroups[gKey] && !q;
                        // Group progress
                        let g2 = 0, g1 = 0, g0 = 0;
                        skills.forEach((s) => {
                          const v = statuses[`${d.domain_id}_${s.skill_id}`];
                          if (v === 2) g2++; else if (v === 1) g1++; else if (v === 0) g0++;
                        });
                        const gTotal = skills.length;
                        const gPct = gTotal ? Math.round(((g2 * 100 + g1 * 50) / (gTotal * 100)) * 100) : 0;
                        return (
                          <div key={gKey} className="rounded-lg bg-slate-50/60 border border-slate-200 overflow-hidden" data-testid={`group-${gKey}`}>
                            <button
                              type="button"
                              onClick={() => setCollapsedGroups((c) => ({ ...c, [gKey]: !c[gKey] }))}
                              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100/60 text-right"
                              data-testid={`group-toggle-${gKey}`}
                            >
                              <div className="flex items-center gap-2">
                                {gCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-400" />}
                                <div className="font-black text-sm text-slate-700">{groupName}</div>
                                <span className="text-[10px] text-slate-500 bg-white border rounded px-1.5">{skills.length}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden hidden md:block">
                                  <div className="h-full rounded-full" style={{ width: `${gPct}%`, background: d.color }} />
                                </div>
                                <div className="text-xs font-black" style={{ color: d.color }}>{gPct}%</div>
                              </div>
                            </button>
                            {!gCollapsed && (
                              <div className="p-3 pt-0 space-y-2">
                                {skills.map((s) => {
                                  const k = `${d.domain_id}_${s.skill_id}`;
                                  const masterKey = `${d.domain_id}::${s.skill_id}`;
                                  const cur = statuses[k];
                                  const mastered = masteredKeys.has(masterKey);
                                  return (
                                    <div key={s.skill_id} className={`rounded-lg p-3 border transition-all ${mastered ? "bg-amber-50 border-amber-300 ring-1 ring-amber-200" : "bg-white border-slate-200"}`}>
                                      <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-[240px] flex items-start gap-2">
                                          {mastered && <span className="text-lg" title="متقن مدى الحياة">🏆</span>}
                                          <span className="font-semibold text-slate-800 text-sm">{s.name}</span>
                                        </div>
                                        <div className="flex gap-1.5 flex-wrap items-center">
                                          <StatusButton current={cur} value={0} onClick={() => setStatus(k, 0)} testid={`status-${k}-0`} />
                                          <StatusButton current={cur} value={1} onClick={() => setStatus(k, 1)} testid={`status-${k}-1`} />
                                          <StatusButton current={cur} value={2} onClick={() => setStatus(k, 2)} testid={`status-${k}-2`} />
                                          {(cur === 2 || mastered) && (
                                            <button
                                              type="button"
                                              onClick={() => toggleMaster(d.domain_id, s.skill_id)}
                                              className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${mastered ? "bg-gradient-to-br from-amber-400 to-yellow-500 border-amber-500 text-white shadow-lg" : "bg-white border-amber-200 text-amber-500 hover:bg-amber-50"}`}
                                              title={mastered ? "متقن مدى الحياة (اضغط للإلغاء)" : "اعتمد كمتقن مدى الحياة"}
                                              data-testid={`master-${k}`}
                                            >
                                              <Sparkles className="w-4 h-4" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      {cur !== undefined && (
                                        <Textarea
                                          value={notes[k] || ""}
                                          onChange={(e) => setNote(k, e.target.value)}
                                          placeholder="ملاحظات على أداء الطفل في هذه المهارة..."
                                          rows={2}
                                          className="mt-2 text-sm bg-slate-50"
                                          data-testid={`note-${k}`}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {skillSearch && domains.every((d) => d.skills.filter((s) => (s.name || "").toLowerCase().includes(skillSearch.toLowerCase()) || (s.group || "").toLowerCase().includes(skillSearch.toLowerCase())).length === 0) && (
              <div className="text-center py-10 text-slate-400 text-sm bg-slate-50 rounded-xl" data-testid="skill-search-empty">لا توجد مهارات مطابقة لبحثك</div>
            )}
          </div>
        )}

        {tab === "qualitative" && (
          <div className="space-y-5 mt-3">
            <BulletEditor
              label="نقاط القوة"
              items={strengths}
              setItems={setStrengths}
              testidPrefix="strengths"
              placeholder="مثال: يظهر حماساً في الأنشطة الجماعية"
              suggestions={[
                "يظهر حماسًا وإقبالاً في الأنشطة الجماعية",
                "تفاعل ممتاز في العلاج المائي",
                "استجابة سريعة للتعزيز الإيجابي",
              ]}
            />
            <BulletEditor
              label="التحديات"
              items={challenges}
              setItems={setChallenges}
              testidPrefix="challenges"
              placeholder="مثال: صعوبة الحفاظ على الانتباه أكثر من 10 دقائق"
              suggestions={[
                "صعوبة المحافظة على الانتباه لأكثر من 10 دقائق",
                "تأخر في التعبير اللفظي",
                "يحتاج توجيه جسدي أثناء الأنشطة الدقيقة",
              ]}
            />
            <BulletEditor
              label="التوصيات"
              items={recommendations}
              setItems={setRecommendations}
              testidPrefix="recommendations"
              placeholder="اكتب توصية أو اختر من البنك أدناه"
              suggestions={domainsData?.recommendation_bank || []}
            />
            <div className="space-y-2">
              <Label className="font-bold text-slate-700">الخلاصة الختامية</Label>
              <Textarea
                value={conclusion}
                onChange={(e) => setConclusion(e.target.value)}
                rows={4}
                placeholder="ملخص شامل لتقدم الطفل، نقاط تميّزه، والتوجه المقترح..."
                data-testid="eval-conclusion"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="eval-cancel">إلغاء</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 gap-2" data-testid="eval-save">
            <FileCheck2 className="w-4 h-4" />
            {saving ? "جاري الحفظ..." : (existing ? "تحديث التقييم" : "حفظ التقييم")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 rounded-lg font-bold text-sm transition-all ${active ? "bg-white text-sky-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}

function SkillsBankDialog({ open, onOpenChange, domainsData, onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [drafts, setDrafts] = useState({});
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(false);

  const setDraft = (dom, val) => setDrafts((d) => ({ ...d, [dom]: val }));
  const startEdit = (key, curName) => setEditing((e) => ({ ...e, [key]: curName }));
  const stopEdit = (key) => setEditing((e) => { const n = { ...e }; delete n[key]; return n; });

  const addSkill = async (dom) => {
    const name = (drafts[dom] || "").trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(`/evaluation-domains/${dom}/skills`, { name });
      toast.success("تمت إضافة المهارة");
      setDraft(dom, "");
      await onRefresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };
  const renameSkill = async (dom, sid) => {
    const key = `${dom}_${sid}`;
    const name = (editing[key] || "").trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.patch(`/evaluation-domains/${dom}/skills/${sid}`, { name });
      toast.success("تم التعديل");
      stopEdit(key);
      await onRefresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };
  const deleteSkill = async (dom, sid) => {
    if (!confirm("حذف المهارة نهائياً من بنك المجال — تأكيد؟")) return;
    setBusy(true);
    try {
      await api.delete(`/evaluation-domains/${dom}/skills/${sid}`);
      toast.success("تم الحذف");
      await onRefresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const domains = domainsData?.domains || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="skills-bank-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Library className="w-6 h-6 text-sky-600" />
            بنك المهارات
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 mt-1">
          خصّص المهارات لكل مجال حسب حاجة المركز. التعديلات ستُطبَّق على التقييمات الجديدة.
        </p>

        <div className="space-y-3 mt-4">
          {domains.map((d) => {
            const isOpen = expanded[d.domain_id];
            return (
              <div key={d.domain_id} className="rounded-xl border-2 overflow-hidden" style={{ borderColor: `${d.color}30` }}>
                <button
                  type="button"
                  onClick={() => setExpanded((e) => ({ ...e, [d.domain_id]: !e[d.domain_id] }))}
                  data-testid={`bank-domain-toggle-${d.domain_id}`}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  style={{ background: isOpen ? `${d.color}08` : "transparent" }}
                >
                  <div className="text-right">
                    <div className="font-black text-lg" style={{ color: d.color }}>{d.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{d.skills.length} مهارة</div>
                  </div>
                  <ArrowRight className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="p-4 space-y-2 border-t" style={{ borderColor: `${d.color}20` }}>
                    {d.skills.map((s) => {
                      const key = `${d.domain_id}_${s.skill_id}`;
                      const isEditing = key in editing;
                      return (
                        <div key={s.skill_id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2.5">
                          {isEditing ? (
                            <>
                              <Input
                                value={editing[key]}
                                onChange={(e) => setEditing((ed) => ({ ...ed, [key]: e.target.value }))}
                                data-testid={`edit-skill-${key}`}
                                className="flex-1 h-9"
                              />
                              <Button size="sm" onClick={() => renameSkill(d.domain_id, s.skill_id)} disabled={busy} data-testid={`save-skill-${key}`} className="gap-1 bg-emerald-600 hover:bg-emerald-700 h-9">
                                <Save className="w-3.5 h-3.5" /> حفظ
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => stopEdit(key)} data-testid={`cancel-skill-${key}`} className="h-9">إلغاء</Button>
                            </>
                          ) : (
                            <>
                              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">{s.skill_id}</span>
                              <span className="flex-1 text-sm font-semibold text-slate-800">{s.name}</span>
                              <Button size="sm" variant="ghost" onClick={() => startEdit(key, s.name)} data-testid={`rename-${key}`} className="h-8 text-slate-600 gap-1">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteSkill(d.domain_id, s.skill_id)} disabled={busy} data-testid={`delete-skill-${key}`} className="h-8 text-rose-600 hover:bg-rose-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-2 pt-1">
                      <Input
                        value={drafts[d.domain_id] || ""}
                        onChange={(e) => setDraft(d.domain_id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(d.domain_id); } }}
                        placeholder="اكتب اسم مهارة جديدة..."
                        data-testid={`add-skill-input-${d.domain_id}`}
                        className="flex-1 h-9"
                      />
                      <Button size="sm" onClick={() => addSkill(d.domain_id)} disabled={busy} data-testid={`add-skill-btn-${d.domain_id}`} className="gap-1 h-9" style={{ background: d.color }}>
                        <Plus className="w-3.5 h-3.5" /> إضافة
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_ABBREV = { 0: "×", 1: "◐", 2: "✓" };
function DeltaPill({ value, unit = "%" }) {
  if (value === 0 || value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
        <Minus className="w-2.5 h-2.5" /> ثابت
      </span>
    );
  }
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${up ? "text-emerald-700 bg-emerald-100" : "text-rose-700 bg-rose-100"}`}>
      {up ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
      {up ? "+" : ""}{value}{unit}
    </span>
  );
}

function StatusCell({ v }) {
  if (v === undefined || v === null) return <span className="text-slate-300 text-xs">—</span>;
  const S = STATUS_COLORS[v];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${S.bg} ${S.text}`}>
      {STATUS_ABBREV[v]} {S.label}
    </span>
  );
}

function CompareDialog({ open, onOpenChange, evalAId, evalBId, childName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !evalAId || !evalBId) return;
    setLoading(true);
    api.get(`/evaluations/compare?a=${evalAId}&b=${evalBId}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(extractError(e)); onOpenChange(false); })
      .finally(() => setLoading(false));
  }, [open, evalAId, evalBId]); // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <GitCompareArrows className="w-6 h-6 text-sky-600" />
            مقارنة تقييمين — {childName}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="text-center py-16 text-slate-400">جاري التحضير...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <Card className="border-slate-300">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">التقييم (أ)</div>
                  <div className="text-lg font-black text-slate-900 mt-1">{data.a.date}</div>
                  <div className="text-xs text-slate-500">{data.a.period_label || "—"} · {data.a.evaluator_name}</div>
                  <div className="text-3xl font-black text-slate-700 mt-2">{data.a.overall_percent}%</div>
                </CardContent>
              </Card>
              <Card className="border-sky-300 bg-gradient-to-br from-sky-50 to-emerald-50">
                <CardContent className="p-4 text-center">
                  <div className="text-xs font-bold text-sky-700 uppercase tracking-wider">التغيّر الإجمالي</div>
                  <div className={`text-5xl font-black mt-2 ${data.overall_delta > 0 ? "text-emerald-700" : data.overall_delta < 0 ? "text-rose-700" : "text-slate-500"}`}>
                    {data.overall_delta > 0 ? "+" : ""}{data.overall_delta}%
                  </div>
                  <div className="text-xs font-bold text-slate-500 mt-1">
                    {data.overall_delta > 0 ? "تقدّم ملحوظ ✨" : data.overall_delta < 0 ? "تراجع طفيف" : "ثابت"}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-sky-400 border-2">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-sky-700 uppercase tracking-wider">التقييم (ب) الأحدث</div>
                  <div className="text-lg font-black text-slate-900 mt-1">{data.b.date}</div>
                  <div className="text-xs text-slate-500">{data.b.period_label || "—"} · {data.b.evaluator_name}</div>
                  <div className="text-3xl font-black text-sky-700 mt-2">{data.b.overall_percent}%</div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-5 space-y-4">
              {data.domains.map((d) => (
                <div key={d.domain_id} className="rounded-xl border-2" style={{ borderColor: `${d.color}30` }}>
                  <div className="p-3 flex items-center justify-between" style={{ background: `${d.color}10` }}>
                    <div>
                      <div className="font-black text-lg" style={{ color: d.color }}>{d.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {d.a_percent}% → <span className="font-black">{d.b_percent}%</span>
                      </div>
                    </div>
                    <DeltaPill value={d.delta_percent} />
                  </div>
                  <div className="p-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] text-slate-500 uppercase tracking-wider">
                          <th className="text-right py-1 font-bold">المهارة</th>
                          <th className="py-1 font-bold w-32">التقييم (أ)</th>
                          <th className="py-1 font-bold w-32">التقييم (ب)</th>
                          <th className="py-1 font-bold w-20">التغيّر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.skills.map((s) => (
                          <tr key={s.skill_id} className="border-t border-slate-100">
                            <td className="py-2 font-semibold text-slate-800">{s.name}</td>
                            <td className="py-2 text-center"><StatusCell v={s.a.status} /></td>
                            <td className="py-2 text-center"><StatusCell v={s.b.status} /></td>
                            <td className="py-2 text-center">
                              {s.delta !== null && s.delta !== undefined && <DeltaPill value={s.delta} unit="" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EvalCard({ ev, canEdit, onEdit, onFreeze, onDelete, isAdmin, selectionMode, selected, onToggleSelect }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/evaluations/${ev.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `تقييم-${ev.child_name}-${ev.date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("تعذر تحميل PDF"); }
    finally { setDownloading(false); }
  };
  return (
    <Card
      className={`card-elevated transition-all ${selectionMode ? (selected ? "border-2 border-sky-500 ring-4 ring-sky-100" : "border-2 border-dashed border-slate-300 hover:border-sky-300 cursor-pointer") : "border-slate-200/70"}`}
      onClick={selectionMode ? onToggleSelect : undefined}
      data-testid={`eval-${ev.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            {selectionMode && (
              <div className={`w-6 h-6 rounded-md border-2 shrink-0 mt-1 flex items-center justify-center ${selected ? "bg-sky-600 border-sky-600" : "bg-white border-slate-300"}`} data-testid={`select-eval-${ev.id}`}>
                {selected && <Check className="w-4 h-4 text-white" />}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="w-3.5 h-3.5" /> {ev.date}
                {ev.period_label && <span className="mx-1">·</span>}
                {ev.period_label && <span>{ev.period_label}</span>}
                {ev.frozen && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                    <Lock className="w-3 h-3" /> مجمّد
                  </span>
                )}
              </div>
              <div className="text-lg font-black text-slate-900 mt-1">{ev.child_name}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <User2 className="w-3.5 h-3.5" /> {ev.evaluator_name}
                {ev.program_type && <span className="mx-1">·</span>}
                {ev.program_type && <span>{ev.program_type}</span>}
              </div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-black text-sky-700">{ev.overall_percent}%</div>
            <div className="text-[10px] font-bold text-slate-500 uppercase">إجمالي</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {(ev.domain_stats || []).map((s) => (
            <div key={s.domain_id} className={`rounded-lg border p-2 relative ${s.percent >= 90 ? "border-amber-300 bg-amber-50/40 ring-1 ring-amber-200" : "border-slate-200"}`}>
              <div className="text-[11px] font-bold text-slate-600 truncate flex items-center gap-1">
                {s.percent >= 90 && <span title="جاهز لشهادة الإنجاز">🏆</span>}
                {s.name}
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-lg font-black" style={{ color: s.color }}>{s.percent}%</span>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full mt-1 overflow-hidden">
                <div className="h-full" style={{ width: `${s.percent}%`, background: s.color }} />
              </div>
              {s.percent >= 90 && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await api.get(`/children/${ev.child_id}/mastery-certificate/${s.domain_id}`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `mastery-${s.domain_id}-${ev.child_id}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("🏆 تم تنزيل شهادة الإنجاز");
                    } catch (e) { toast.error(extractError(e)); }
                  }}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-gradient-to-l from-amber-500 to-yellow-600 text-white text-[10px] font-black hover:from-amber-600 hover:to-yellow-700 transition-all shadow"
                  data-testid={`mastery-cert-${ev.id}-${s.domain_id}`}
                  title={`تنزيل شهادة إنجاز مجال ${s.name}`}
                >
                  <Award className="w-3 h-3" /> شهادة إنجاز
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
          <Button variant="outline" size="sm" onClick={download} disabled={downloading} className="gap-1.5 text-sky-700 border-sky-200" data-testid={`download-eval-${ev.id}`}>
            <Download className="w-3.5 h-3.5" /> {downloading ? "..." : "PDF"}
          </Button>
          {canEdit && !ev.frozen && (
            <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5" data-testid={`edit-eval-${ev.id}`}>
              <Pencil className="w-3.5 h-3.5" /> تعديل
            </Button>
          )}
          {canEdit && !ev.frozen && (
            <Button variant="outline" size="sm" onClick={onFreeze} className="gap-1.5 text-slate-700" data-testid={`freeze-eval-${ev.id}`}>
              <Lock className="w-3.5 h-3.5" /> تجميد
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={onDelete} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`delete-eval-${ev.id}`}>
              <Trash2 className="w-3.5 h-3.5" /> حذف
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function EvaluationsPage() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [evals, setEvals] = useState([]);
  const [domainsData, setDomainsData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canCreate = user.role === "admin" || user.role === "teacher";
  const isAdmin = user.role === "admin";
  const [bankOpen, setBankOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [archiveYear, setArchiveYear] = useState("all");
  const [bulkCertOpen, setBulkCertOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id]; // rolling window of 2
      return [...prev, id];
    });
  };
  const exitSelection = () => { setSelectionMode(false); setSelectedIds([]); };

  const loadDomains = async () => {
    const dom = await api.get("/evaluation-domains");
    setDomainsData(dom.data);
  };

  useEffect(() => {
    (async () => {
      const [kids, dom] = await Promise.all([
        api.get("/children"),
        api.get("/evaluation-domains"),
      ]);
      setChildren(kids.data);
      setDomainsData(dom.data);
      if (kids.data.length) setSelected(kids.data[0]);
    })();
  }, []);

  const loadEvals = async () => {
    if (!selected) { setEvals([]); return; }
    const { data } = await api.get(`/evaluations?child_id=${selected.id}`);
    setEvals(data);
    try {
      const y = await api.get(`/evaluations/available-years?child_id=${selected.id}`);
      setAvailableYears(y.data.years || []);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { loadEvals(); }, [selected]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setDialogOpen(true); };

  const freeze = async (ev) => {
    if (!confirm("سيتم تجميد التقييم ولن يمكن تعديله لاحقاً — هل أنت متأكد؟")) return;
    try {
      await api.post(`/evaluations/${ev.id}/freeze`);
      toast.success("تم تجميد التقييم");
      loadEvals();
    } catch (e) { toast.error(extractError(e)); }
  };
  const del = async (ev) => {
    if (!confirm("حذف نهائي لهذا التقييم — تأكيد؟")) return;
    try {
      await api.delete(`/evaluations/${ev.id}`);
      toast.success("تم الحذف");
      loadEvals();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="evaluations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-sky-600" />
            التقييمات الإلكترونية
          </h1>
          <p className="text-slate-500 mt-2">
            تقييم تراكمي لكل طفل عبر ٧ مجالات — يحفظ سجلاً تاريخياً كاملاً لتقدّمه
          </p>
        </div>
        {canCreate && selected && (
          <div className="flex flex-wrap items-center gap-2">
            {!selectionMode && evals.length > 0 && (
              <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-xl h-12 pe-1 ps-2" data-testid="archive-controls">
                <select
                  value={archiveYear}
                  onChange={(e) => setArchiveYear(e.target.value)}
                  data-testid="archive-year-select"
                  className="bg-transparent text-emerald-800 text-sm font-bold outline-none py-2 pe-1 cursor-pointer"
                >
                  <option value="all">كل السنوات</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}/{y + 1}</option>
                  ))}
                </select>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    try {
                      const url = archiveYear === "all"
                        ? `/evaluations/child/${selected.id}/archive.pdf`
                        : `/evaluations/child/${selected.id}/archive.pdf?year_start=${archiveYear}`;
                      const r = await api.get(url, { responseType: "blob" });
                      const blob = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = blob;
                      a.download = `أرشيف-تقييمات-${selected.name}${archiveYear === "all" ? "" : `-${archiveYear}`}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(blob), 5000);
                      toast.success("تم تصدير الأرشيف");
                    } catch (e) { toast.error(extractError(e)); }
                  }}
                  className="h-10 gap-1.5 text-emerald-700 hover:bg-emerald-100"
                  data-testid="download-archive-btn"
                >
                  <Archive className="w-4 h-4" /> تصدير
                </Button>
              </div>
            )}
            {!selectionMode && isAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.get("/evaluations/export-analysis.xlsx", { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "تحليل-التقييمات.xlsx";
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                    toast.success("تم تصدير التحليل");
                  } catch (e) { toast.error(extractError(e)); }
                }}
                className="h-12 gap-2 text-emerald-700 border-emerald-200"
                data-testid="excel-analysis-btn"
              >
                <FileCheck2 className="w-4 h-4" /> XLSX تحليل
              </Button>
            )}
            {!selectionMode && isAdmin && (
              <Button
                variant="outline"
                onClick={() => setBankOpen(true)}
                className="h-12 gap-2 text-slate-800 border-slate-300"
                data-testid="skills-bank-btn"
              >
                <Library className="w-4 h-4" /> بنك المهارات
              </Button>
            )}
            {!selectionMode ? (
              <Button
                variant="outline"
                onClick={() => setSelectionMode(true)}
                className="h-12 gap-2 text-sky-700 border-sky-200"
                data-testid="compare-mode-btn"
              >
                <GitCompareArrows className="w-4 h-4" /> مقارنة
              </Button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-3 py-1.5 text-xs font-bold" data-testid="selection-hint">
                  اختر تقييمين ({selectedIds.length}/2)
                </span>
                <Button
                  onClick={() => setCompareOpen(true)}
                  disabled={selectedIds.length !== 2}
                  className="h-12 gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="run-compare-btn"
                >
                  <GitCompareArrows className="w-4 h-4" /> ابدأ المقارنة
                </Button>
                <Button variant="outline" onClick={exitSelection} className="h-12" data-testid="exit-compare-btn">إلغاء</Button>
              </>
            )}
            {!selectionMode && isAdmin && (
              <Button
                onClick={() => setBulkCertOpen(true)}
                variant="outline"
                className="h-12 gap-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="bulk-cert-print-btn"
              >
                <Award className="w-4 h-4" /> شهادات جماعية
              </Button>
            )}
            {!selectionMode && (
              <Button onClick={openNew} className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-evaluation-btn">
                <Plus className="w-4 h-4" /> تقييم جديد
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200/70 md:col-span-1">
          <CardContent className="p-3 space-y-1">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 px-2">الأطفال</div>
            {children.map((c) => (
              <button
                key={c.id}
                data-testid={`eval-child-${c.id}`}
                onClick={() => setSelected(c)}
                className={`w-full text-right p-3 rounded-lg transition-all ${selected?.id === c.id ? "bg-sky-600 text-white shadow-md shadow-sky-100" : "hover:bg-slate-100 text-slate-800"}`}
              >
                <div className="font-bold">{c.name}</div>
                <div className={`text-xs ${selected?.id === c.id ? "text-sky-100" : "text-slate-500"}`}>{c.disability_type || "—"}</div>
              </button>
            ))}
            {children.length === 0 && <div className="text-sm text-slate-400 text-center py-6">لا يوجد أطفال</div>}
          </CardContent>
        </Card>

        <div className="md:col-span-3 space-y-4">
          {evals.length === 0 ? (
            <Card>
              <CardContent className="p-16 text-center text-slate-400">
                لا توجد تقييمات لهذا الطفل بعد
                {canCreate && <div className="mt-3 text-xs">اضغط "تقييم جديد" للبدء</div>}
              </CardContent>
            </Card>
          ) : (
            evals.map((ev) => (
              <EvalCard
                key={ev.id}
                ev={ev}
                canEdit={canCreate && (isAdmin || ev.evaluator_id === user.id)}
                isAdmin={isAdmin}
                onEdit={() => openEdit(ev)}
                onFreeze={() => freeze(ev)}
                onDelete={() => del(ev)}
                selectionMode={selectionMode}
                selected={selectedIds.includes(ev.id)}
                onToggleSelect={() => toggleSelect(ev.id)}
              />
            ))
          )}
        </div>
      </div>

      {selected && domainsData && (
        <EvaluationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          child={selected}
          existing={editing}
          domainsData={domainsData}
          onSaved={loadEvals}
        />
      )}
      {domainsData && (
        <SkillsBankDialog
          open={bankOpen}
          onOpenChange={setBankOpen}
          domainsData={domainsData}
          onRefresh={loadDomains}
        />
      )}
      <CompareDialog
        open={compareOpen}
        onOpenChange={(v) => { setCompareOpen(v); if (!v) exitSelection(); }}
        evalAId={selectedIds[0]}
        evalBId={selectedIds[1]}
        childName={selected?.name}
      />
      <BulkCertificatePrintDialog
        open={bulkCertOpen}
        onClose={() => setBulkCertOpen(false)}
      />
    </div>
  );
}
